import { Box } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../../context/AuthContext";
import { useSites, useCreateSite, useEditSite } from "../../../hooks/useSite";
import { useCreateExternalStorage } from "../../../hooks/useExternalStorage";
import type Site from "../../../types/site/Site";
import { StorageType } from "../../../types/external-storage/StorageType";
import {
  toolbarBtnSx,
  flyoutInputSx,
  flyoutLabelSx,
  crudTableSx,
  selectedRowSx,
  radioInputSx,
  primaryCellSx,
  saveBtnSx,
  CrudPageLayout,
  LoadingState,
  EmptyState,
  FlyoutDrawer,
  FlyoutFooter,
  PlusIcon,
  tabSx,
  formErrorSx,
} from "../../components/shared";

// ---------------------------------------------------------------------------
// Storage type display label — must be called with t from within component
// ---------------------------------------------------------------------------

function storageTypeLabel(type: StorageType, t: (key: string) => string): string {
  switch (type) {
    case StorageType.ExternalAmazonS3:
      return t("configureStorage:amazonS3");
    case StorageType.AzureBlob:
      return t("configureStorage:azureBlob");
    case StorageType.GCPCloud:
      return t("configureStorage:gcpCloud");
    default:
      return String(type);
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

type AwsAuthType = "AWSAccessKeys" | "AWSCrossAccount";

export default function V2Sites() { // NOSONAR
  const { t } = useTranslation(["configureStorage", "common", "errors"]);
  const { customerId } = useParams<{ customerId: string }>();
  const { user: authUser } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // -- Data -----------------------------------------------------------------
  const { data: sitesData, isLoading: sitesLoading } = useSites(customerId);
  const sites = sitesData?.sites ?? [];

  // -- Mutations ------------------------------------------------------------
  const createSiteMutation = useCreateSite();
  const editSiteMutation = useEditSite();
  const storageMutation = useCreateExternalStorage();

  // -- Selection ------------------------------------------------------------
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const selectedSite = sites.find((s) => s.siteId === selectedSiteId) ?? null;

  // -- Flyout state ---------------------------------------------------------
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutMode, setFlyoutMode] = useState<"create" | "edit">("create");

  // -- Flyout form state (Site tab) -----------------------------------------
  const [formName, setFormName] = useState("");
  const [formArchivePathPrefix, setFormArchivePathPrefix] = useState("");
  const [formError, setFormError] = useState("");
  const [activeTab, setActiveTab] = useState<"site" | "storage">("site");
  const [storageSuccess, setStorageSuccess] = useState("");

  // -- Storage form state ---------------------------------------------------
  const [storageType, setStorageType] = useState<StorageType>(StorageType.ExternalAmazonS3);
  const [authType, setAuthType] = useState<AwsAuthType>("AWSAccessKeys");
  const [bucketName, setBucketName] = useState("");
  const [bucketPrefix, setBucketPrefix] = useState("");
  const [awsRegion, setAwsRegion] = useState("");
  const [awsAccessKey, setAwsAccessKey] = useState("");
  const [awsSecretKey, setAwsSecretKey] = useState("");
  const [awsRoleArn, setAwsRoleArn] = useState("");
  const [awsExternalId, setAwsExternalId] = useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : ""
  );
  const [azureTenantId, setAzureTenantId] = useState("");
  const [azureClientId, setAzureClientId] = useState("");
  const [azureClientSecret, setAzureClientSecret] = useState("");
  const [azureContainerName, setAzureContainerName] = useState("");
  const [azureStorageAccountName, setAzureStorageAccountName] = useState("");
  const [azurePrefix, setAzurePrefix] = useState("");
  const [gcpServiceAccountKey, setGcpServiceAccountKey] = useState("");

  // -- Delete state ---------------------------------------------------------
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);

  // -- Permissions ----------------------------------------------------------
  const canCreate = authUser?.hasPermission("sites:Create") ?? false;
  const canEdit = authUser?.hasPermission("sites:Update") ?? false;
  const canDelete = authUser?.hasPermission("sites:Delete") ?? false;

  // -- Reset helpers --------------------------------------------------------
  function resetStorageFields() {
    setBucketName("");
    setBucketPrefix("");
    setAwsRegion("");
    setAwsAccessKey("");
    setAwsSecretKey("");
    setAwsRoleArn("");
    setAwsExternalId(
      typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : ""
    );
    setAzureTenantId("");
    setAzureClientId("");
    setAzureClientSecret("");
    setAzureContainerName("");
    setAzureStorageAccountName("");
    setAzurePrefix("");
    setGcpServiceAccountKey("");
  }

  const resetForm = () => {
    setFormName("");
    setFormArchivePathPrefix("");
    setFormError("");
    setActiveTab("site");
    setStorageSuccess("");
    setShowDeleteMessage(false);
    resetStorageFields();
  };

  const populateFromSite = (site: Site) => {
    setFormName(site.name);
    setFormError("");
    setActiveTab("site");
    setStorageSuccess("");
    setShowDeleteMessage(false);
    resetStorageFields();
  };

  // -- Handlers -------------------------------------------------------------
  const handleRowSelect = (siteId: string) => {
    if (!canEdit) {
      setSelectedSiteId((prev) => (prev === siteId ? null : siteId));
      return;
    }
    const site = sites.find((s) => s.siteId === siteId);
    if (!site) return;

    if (selectedSiteId === siteId && flyoutOpen) {
      setFlyoutOpen(false);
      setSelectedSiteId(null);
      return;
    }

    setSelectedSiteId(siteId);
    setFlyoutMode("edit");
    setFlyoutOpen(true);
    populateFromSite(site);
  };

  useEffect(() => {
    if (flyoutOpen && flyoutMode === "edit" && selectedSite) {
      populateFromSite(selectedSite);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite?.siteId]);

  const handleCreate = () => {
    setSelectedSiteId(null);
    setFlyoutMode("create");
    setFlyoutOpen(true);
    resetForm();
  };

  const handleCloseFlyout = () => {
    setFlyoutOpen(false);
    resetForm();
  };

  const handleSaveSite = () => {
    if (!formName.trim()) {
      setFormError(t("configureStorage:nameRequired"));
      return;
    }
    setFormError("");

    if (flyoutMode === "create") {
      const trimmedPrefix = formArchivePathPrefix.trim();
      createSiteMutation.mutate(
        {
          customerId: customerId ?? "",
          name: formName.trim(),
          archivePathPrefix: trimmedPrefix || undefined,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sites"] });
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err) => setFormError(err.message || t("configureStorage:failedToCreateSite")),
        },
      );
    } else if (selectedSite) {
      editSiteMutation.mutate(
        {
          customerId: customerId ?? "",
          siteId: selectedSite.siteId,
          name: formName.trim(),
          storageConfig: selectedSite.storageConfig ?? null,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sites"] });
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err) => setFormError(err.message || t("configureStorage:failedToUpdateSite")),
        },
      );
    }
  };

  const handleStorageTypeChange = (newType: StorageType) => {
    setStorageType(newType);
    setFormError("");
    resetStorageFields();
  };

  const handleAuthTypeChange = (newAuthType: AwsAuthType) => {
    setAuthType(newAuthType);
    setFormError("");
    setAwsAccessKey("");
    setAwsSecretKey("");
    setAwsRoleArn("");
    setAwsExternalId(
      typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : ""
    );
  };

  const handleSaveStorage = () => { // NOSONAR
    if (!selectedSite) return;
    setFormError("");
    setStorageSuccess("");

    let payload: Record<string, unknown> | null = null;

    if (storageType === StorageType.ExternalAmazonS3) {
      if (authType === "AWSAccessKeys") {
        if (!bucketName.trim()) { setFormError(t("configureStorage:bucketNameRequired")); return; }
        if (!awsRegion.trim()) { setFormError(t("configureStorage:regionRequired")); return; }
        if (!awsAccessKey.trim()) { setFormError(t("configureStorage:accessKeyRequired")); return; }
        if (!awsSecretKey.trim()) { setFormError(t("configureStorage:secretKeyRequired")); return; }
        payload = {
          customerId,
          siteId: selectedSite.siteId,
          SiteId: selectedSite.siteId,
          StorageType: StorageType.ExternalAmazonS3,
          AWSAccessKeys: {
            AWSBucketName: bucketName.trim(),
            AWSBucketPrefix: bucketPrefix.trim() || undefined,
            Region: awsRegion.trim(),
            AccessKey: awsAccessKey.trim(),
            SecretKey: awsSecretKey.trim(),
          },
        };
      } else {
        if (!bucketName.trim()) { setFormError(t("configureStorage:bucketNameRequired")); return; }
        if (!awsRegion.trim()) { setFormError(t("configureStorage:regionRequired")); return; }
        if (!awsExternalId.trim()) { setFormError(t("configureStorage:externalIdRequired")); return; }
        if (!awsRoleArn.trim()) { setFormError(t("configureStorage:roleArnRequired")); return; }
        payload = {
          customerId,
          siteId: selectedSite.siteId,
          SiteId: selectedSite.siteId,
          StorageType: StorageType.ExternalAmazonS3,
          AWSCrossAccount: {
            AWSBucketName: bucketName.trim(),
            AWSBucketPrefix: bucketPrefix.trim() || undefined,
            Region: awsRegion.trim(),
            ExternalId: awsExternalId.trim(),
            RoleArn: awsRoleArn.trim(),
          },
        };
      }
    } else if (storageType === StorageType.AzureBlob) {
      if (!azureTenantId.trim()) { setFormError(t("configureStorage:tenantIdRequired")); return; }
      if (!azureClientId.trim()) { setFormError(t("configureStorage:clientIdRequired")); return; }
      if (!azureClientSecret.trim()) { setFormError(t("configureStorage:clientSecretRequired")); return; }
      if (!azureContainerName.trim()) { setFormError(t("configureStorage:containerNameRequired")); return; }
      if (!azureStorageAccountName.trim()) { setFormError(t("configureStorage:storageAccountNameRequired")); return; }
      payload = {
        customerId,
        siteId: selectedSite.siteId,
        SiteId: selectedSite.siteId,
        StorageType: StorageType.AzureBlob,
        AzureBlob: {
          TenantId: azureTenantId.trim(),
          ClientId: azureClientId.trim(),
          ClientSecret: azureClientSecret.trim(),
          ContainerName: azureContainerName.trim(),
          StorageAccountName: azureStorageAccountName.trim(),
          AzurePrefix: azurePrefix.trim() || undefined,
        },
      };
    } else if (storageType === StorageType.GCPCloud) {
      if (!bucketName.trim()) { setFormError(t("configureStorage:bucketNameRequired")); return; }
      if (!gcpServiceAccountKey.trim()) { setFormError(t("configureStorage:serviceAccountKeyRequired")); return; }
      try {
        JSON.parse(gcpServiceAccountKey);
      } catch {
        setFormError(t("configureStorage:invalidJsonFormat"));
        return;
      }
      payload = {
        customerId,
        siteId: selectedSite.siteId,
        SiteId: selectedSite.siteId,
        StorageType: StorageType.GCPCloud,
        GCPServiceAccountFile: {
          gcpBucketName: bucketName.trim(),
          gcpBucketPrefix: bucketPrefix.trim() || undefined,
          serviceAccountKeyFile: JSON.parse(gcpServiceAccountKey),
        },
      };
    }

    if (!payload) return;

    storageMutation.mutate(
      payload as { customerId: string; siteId: string },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["sites"] });
          setStorageSuccess(t("configureStorage:storageConfiguredSuccessfully"));
          resetStorageFields();
        },
        onError: (err) => setFormError(err.message || t("configureStorage:failedToConfigureStorage")),
      },
    );
  };

  const isSaving = createSiteMutation.isPending || editSiteMutation.isPending || storageMutation.isPending;

  const bucketFields = (placeholder: string) => (
    <>
      <Box>
        <Box sx={flyoutLabelSx}>{t("configureStorage:bucketName")} *</Box>
        <Box component="input" type="text" placeholder={placeholder} value={bucketName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBucketName(e.target.value)} sx={flyoutInputSx} />
      </Box>
      <Box>
        <Box sx={flyoutLabelSx}>{t("configureStorage:bucketPrefix")}</Box>
        <Box component="input" type="text" placeholder="optional/prefix/" value={bucketPrefix}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBucketPrefix(e.target.value)} sx={flyoutInputSx} />
      </Box>
    </>
  );

  // --- Render storage form fields based on type ---
  const renderStorageFields = () => {
    if (storageType === StorageType.ExternalAmazonS3) {
      return (
        <>
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:authenticationMethod")}</Box>
            <Box
              component="select"
              value={authType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleAuthTypeChange(e.target.value as AwsAuthType)
              }
              sx={{ ...flyoutInputSx, appearance: "auto" as const }}
            >
              <option value="AWSAccessKeys">{t("configureStorage:awsAccessKeys")}</option>
              <option value="AWSCrossAccount">{t("configureStorage:awsCrossAccount")}</option>
            </Box>
          </Box>
          {bucketFields("my-bucket-name")}
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:region")} *</Box>
            <Box
              component="input"
              type="text"
              placeholder="us-east-1"
              value={awsRegion}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAwsRegion(e.target.value)}
              sx={flyoutInputSx}
            />
          </Box>
          {authType === "AWSAccessKeys" ? (
            <>
              <Box>
                <Box sx={flyoutLabelSx}>{t("configureStorage:accessKey")} *</Box>
                <Box
                  component="input"
                  type="text"
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  value={awsAccessKey}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAwsAccessKey(e.target.value)}
                  sx={flyoutInputSx}
                />
              </Box>
              <Box>
                <Box sx={flyoutLabelSx}>{t("configureStorage:secretKey")} *</Box>
                <Box
                  component="input"
                  type="password"
                  placeholder={t("configureStorage:secretKey")}
                  value={awsSecretKey}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAwsSecretKey(e.target.value)}
                  sx={flyoutInputSx}
                />
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Box sx={flyoutLabelSx}>{t("configureStorage:externalId")} *</Box>
                <Box
                  component="input"
                  type="text"
                  value={awsExternalId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAwsExternalId(e.target.value)}
                  sx={{ ...flyoutInputSx, fontFamily: "monospace" }}
                />
              </Box>
              <Box>
                <Box sx={flyoutLabelSx}>{t("configureStorage:roleArn")} *</Box>
                <Box
                  component="input"
                  type="text"
                  placeholder="arn:aws:iam::123456789012:role/ExampleRole"
                  value={awsRoleArn}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAwsRoleArn(e.target.value)}
                  sx={flyoutInputSx}
                />
              </Box>
            </>
          )}
        </>
      );
    }

    if (storageType === StorageType.AzureBlob) {
      return (
        <>
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:tenantId")} *</Box>
            <Box
              component="input"
              type="text"
              placeholder={t("configureStorage:tenantId")}
              value={azureTenantId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzureTenantId(e.target.value)}
              sx={flyoutInputSx}
            />
          </Box>
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:clientIdLabel")} *</Box>
            <Box
              component="input"
              type="text"
              placeholder={t("configureStorage:clientId")}
              value={azureClientId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzureClientId(e.target.value)}
              sx={flyoutInputSx}
            />
          </Box>
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:clientSecret")} *</Box>
            <Box
              component="input"
              type="password"
              placeholder={t("configureStorage:clientSecret")}
              value={azureClientSecret}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzureClientSecret(e.target.value)}
              sx={flyoutInputSx}
            />
          </Box>
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:containerName")} *</Box>
            <Box
              component="input"
              type="text"
              placeholder="my-container"
              value={azureContainerName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzureContainerName(e.target.value)}
              sx={flyoutInputSx}
            />
          </Box>
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:storageAccountName")} *</Box>
            <Box
              component="input"
              type="text"
              placeholder="mystorageaccount"
              value={azureStorageAccountName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzureStorageAccountName(e.target.value)}
              sx={flyoutInputSx}
            />
          </Box>
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:azurePrefix")}</Box>
            <Box
              component="input"
              type="text"
              placeholder="optional/prefix/"
              value={azurePrefix}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzurePrefix(e.target.value)}
              sx={flyoutInputSx}
            />
          </Box>
        </>
      );
    }

    if (storageType === StorageType.GCPCloud) {
      return (
        <>
          {bucketFields("my-gcp-bucket")}
          <Box>
            <Box sx={flyoutLabelSx}>{t("configureStorage:serviceAccountKeyFile")} *</Box>
            <Box
              component="textarea"
              placeholder={'{\n  "type": "service_account",\n  "project_id": "...",\n  ...\n}'}
              value={gcpServiceAccountKey}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setGcpServiceAccountKey(e.target.value)}
              sx={{
                ...flyoutInputSx,
                fontFamily: "monospace",
                fontSize: "0.8125rem",
                minHeight: "120px",
                resize: "vertical",
              }}
            />
          </Box>
        </>
      );
    }

    return null;
  };

  // -- Render helpers -------------------------------------------------------
  const sitesListEmptyOrTable = sites.length === 0 ? (
    <EmptyState message={t("configureStorage:noSitesFound")} />
  ) : (
    <Box component="table" sx={crudTableSx}>
      <thead>
        <tr>
          <Box component="th" sx={{ width: 40, textAlign: "center" }}>
            {/* Radio header — intentionally blank */}
          </Box>
          <th>{t("configureStorage:name")}</th>
          <th>{t("grid:siteId")}</th>
          <th>{t("common:archivePathPrefix")}</th>
          <th>{t("configureStorage:storagePlatform")}</th>
          <th>{t("configureStorage:bucketName")}</th>
        </tr>
      </thead>
      <tbody>
        {sites.map((s: Site) => {
          const isSelected = selectedSiteId === s.siteId;

          return (
            <Box
              component="tr"
              key={s.siteId}
              onClick={() => handleRowSelect(s.siteId)}
              sx={selectedRowSx(isSelected)}
            >
              {/* Radio */}
              <Box component="td" sx={{ textAlign: "center" }}>
                <Box
                  component="input"
                  type="radio"
                  name="site-select"
                  checked={isSelected}
                  onChange={() => handleRowSelect(s.siteId)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={radioInputSx}
                />
              </Box>

              {/* Name */}
              <Box component="td" sx={primaryCellSx}>
                {s.name}
              </Box>

              {/* Site ID */}
              <Box component="td" sx={{ fontFamily: "monospace" }}>
                {s.siteId}
              </Box>

              {/* Archive Path Prefix */}
              <Box component="td" sx={{ fontFamily: "monospace" }}>
                {s.archivePathPrefix ?? "\u2014"}
              </Box>

              {/* Storage Platform */}
              <td>
                {s.storageConfig
                  ? storageTypeLabel(s.storageConfig.storageType, t)
                  : "\u2014"}
              </td>

              {/* Bucket Name */}
              <Box component="td" sx={{ fontFamily: "monospace" }}>
                {s.storageConfig?.bucketName ?? "\u2014"}
              </Box>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );

  const saveSiteButtonSavingLabel = flyoutMode === "create"
    ? t("common:creating")
    : t("common:saving");
  const saveSiteButtonLabel = isSaving
    ? saveSiteButtonSavingLabel
    : flyoutMode === "create"
      ? t("configureStorage:createSite")
      : t("configureStorage:saveSite");

  // -- Render ---------------------------------------------------------------

  const flyoutTitle = flyoutMode === "create" ? t("configureStorage:createSite") : t("configureStorage:editSite");

  return (
    <CrudPageLayout
      toolbar={
        canCreate ? (
          <Box component="button" onClick={handleCreate} sx={toolbarBtnSx(false)}>
            <PlusIcon />
            {t("configureStorage:createSite")}
          </Box>
        ) : null
      }
      extra={
        <FlyoutDrawer
          open={flyoutOpen}
          onClose={handleCloseFlyout}
          title={flyoutTitle}
          footer={
            <FlyoutFooter
              onCancel={handleCloseFlyout}
              cancelLabel={t("common:cancel")}
              onSave={(flyoutMode === "create" || activeTab === "site") ? handleSaveSite : undefined}
              saveLabel={(flyoutMode === "create" || activeTab === "site") ? saveSiteButtonLabel : undefined}
              isSaving={isSaving}
              showDelete={flyoutMode === "edit" && canDelete && !showDeleteMessage}
              onDelete={() => setShowDeleteMessage(true)}
              deleteLabel={t("common:delete")}
              extraSaveButtons={
                flyoutMode === "edit" && activeTab === "storage" && selectedSite && !selectedSite.storageConfig ? (
                  <Box
                    component="button"
                    onClick={handleSaveStorage}
                    disabled={isSaving}
                    sx={saveBtnSx}
                  >
                    {storageMutation.isPending ? t("configureStorage:configuringStorage") : t("configureStorage:saveStorage")}
                  </Box>
                ) : undefined
              }
            />
          }
        >
            {/* Tabs (edit mode only) */}
            {flyoutMode === "edit" && (
              <Box
                sx={{
                  display: "flex",
                  gap: 0,
                  borderBottom: "1px solid var(--border-subtle)",
                }}
              >
                <Box
                  component="button"
                  onClick={() => {
                    setActiveTab("site");
                    setFormError("");
                    setStorageSuccess("");
                  }}
                  sx={tabSx(activeTab === "site")}
                >
                  {t("common:site")}
                </Box>
                <Box
                  component="button"
                  onClick={() => {
                    setActiveTab("storage");
                    setFormError("");
                    setStorageSuccess("");
                  }}
                  sx={tabSx(activeTab === "storage")}
                >
                  {t("common:storage")}
                </Box>
              </Box>
            )}

            {formError && (
              <Box sx={formErrorSx}>{formError}</Box>
            )}
            {storageSuccess && (
              <Box sx={{ color: "var(--accent)", fontSize: "0.8125rem" }}>{storageSuccess}</Box>
            )}

            {/* Create mode: Name + Archive Path Prefix */}
            {flyoutMode === "create" && (
              <>
                <Box>
                  <Box sx={flyoutLabelSx}>{t("configureStorage:name")}</Box>
                  <Box
                    component="input"
                    type="text"
                    placeholder={t("configureStorage:sitePlaceholder")}
                    value={formName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormName(e.target.value)}
                    sx={flyoutInputSx}
                  />
                </Box>
                <Box>
                  <Box sx={flyoutLabelSx}>{t("common:archivePathPrefix")}</Box>
                  <Box
                    component="input"
                    type="text"
                    value={formArchivePathPrefix}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormArchivePathPrefix(e.target.value)
                    }
                    sx={flyoutInputSx}
                  />
                  <Box
                    sx={{
                      fontSize: "0.75rem",
                      color: "var(--text-tertiary)",
                      mt: "4px",
                      lineHeight: 1.4,
                    }}
                  >
                    {t("common:archivePathPrefixHelper")}
                  </Box>
                </Box>
              </>
            )}

            {/* Edit mode: Site tab */}
            {flyoutMode === "edit" && activeTab === "site" && (
              <Box>
                <Box sx={flyoutLabelSx}>{t("configureStorage:name")}</Box>
                <Box
                  component="input"
                  type="text"
                  placeholder={t("configureStorage:sitePlaceholder")}
                  value={formName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormName(e.target.value)}
                  sx={flyoutInputSx}
                />
              </Box>
            )}

            {/* Edit mode: Storage tab */}
            {flyoutMode === "edit" && activeTab === "storage" && selectedSite && (
              <>
                {selectedSite.storageConfig ? (
                  /* Read-only display of existing storage config */
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <Box>
                      <Box sx={flyoutLabelSx}>{t("configureStorage:storageType")}</Box>
                      <Box sx={{ fontSize: "0.875rem", color: "var(--text-primary)", py: "8px" }}>
                        {storageTypeLabel(selectedSite.storageConfig.storageType, t)}
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={flyoutLabelSx}>{t("configureStorage:bucketName")}</Box>
                      <Box sx={{ fontSize: "0.875rem", color: "var(--text-primary)", fontFamily: "monospace", py: "8px" }}>
                        {selectedSite.storageConfig.bucketName}
                      </Box>
                    </Box>
                    {selectedSite.storageConfig.externalId && (
                      <Box>
                        <Box sx={flyoutLabelSx}>{t("configureStorage:externalId")}</Box>
                        <Box sx={{ fontSize: "0.875rem", color: "var(--text-primary)", fontFamily: "monospace", py: "8px" }}>
                          {selectedSite.storageConfig.externalId}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  /* Storage configuration form */
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <Box>
                      <Box sx={flyoutLabelSx}>{t("configureStorage:storageType")}</Box>
                      <Box
                        component="select"
                        value={storageType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          handleStorageTypeChange(e.target.value as StorageType)
                        }
                        sx={{ ...flyoutInputSx, appearance: "auto" as const }}
                      >
                        <option value={StorageType.ExternalAmazonS3}>{t("configureStorage:amazonS3")}</option>
                        <option value={StorageType.AzureBlob}>{t("configureStorage:azureBlob")}</option>
                        <option value={StorageType.GCPCloud}>{t("configureStorage:gcpCloud")}</option>
                      </Box>
                    </Box>
                    {renderStorageFields()}
                  </Box>
                )}
              </>
            )}

            {/* Delete message */}
            {flyoutMode === "edit" && showDeleteMessage && (
              <Box
                sx={{
                  borderTop: "1px solid var(--border-subtle)",
                  pt: "16px",
                  mt: "4px",
                }}
              >
                <Box sx={{ color: "var(--text-secondary)", fontSize: "0.875rem", lineHeight: 1.6 }}>
                  {t("configureStorage:deleteContactSupport")}{" "}
                  <Box
                    component="a"
                    href="mailto:support@wilmactech.com"
                    sx={{ color: "var(--accent)", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    support@wilmactech.com
                  </Box>
                  .
                </Box>
              </Box>
            )}
        </FlyoutDrawer>
      }
    >
      {sitesLoading ? (
        <LoadingState message={t("configureStorage:loadingSites")} />
      ) : sitesListEmptyOrTable}
    </CrudPageLayout>
  );
}
